import RecurringTaskCompletionModal from '@/components/modals/recurring-task-completion-modal';
import TaskHistoryModal from '@/components/task-history-modal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import type { TaskWithRelations, User } from '@shared/schema';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarPlus,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  FolderOpen,
  HelpCircle,
  History,
  MessageCircle,
  Play,
  Repeat,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
// Drag and drop functionality commented out - can be enabled in future if needed
// import { useDrag } from 'react-dnd';

interface TaskCardProps {
  task: TaskWithRelations;
  currentUser?: User;
  isHelpTask?: boolean; // Whether this is a task the user is helping with
  canTrackTime?: boolean; // Whether the user can track time for this task
  onStatusUpdate?: (taskId: string, status: string) => void;
  onEdit?: (task: TaskWithRelations) => void;
  onView?: (task: TaskWithRelations) => void;
  onNeedHelp?: (task: TaskWithRelations) => void;
  onAssignToMe?: (taskId: string) => void;
  onPriorityUpdate?: (taskId: string, priority: number) => void;
  globalCollapsed?: boolean; // Global collapse state from parent
  isDraggable?: boolean; // Whether this task can be dragged
  taskNumber?: number; // Task number for display
  hasUnreadComments?: boolean; // Show alert dot when new comment added
}

export default function TaskCard({
  task,
  currentUser,
  isHelpTask = false,
  canTrackTime = false,
  onStatusUpdate,
  onEdit,
  onView,
  onNeedHelp,
  onAssignToMe,
  onPriorityUpdate,
  globalCollapsed = false,
  isDraggable = true,
  taskNumber,
  hasUnreadComments = false,
}: TaskCardProps & { readOnly?: boolean }) {
  // Fetch teams to display team information
  const { data: teams = [] } = useQuery<any[]>({
    queryKey: ['/api/teams'],
    enabled: !!(task as any).teamId,
  });
  const { toast } = useToast();

  // Debug: Log viewer status for GM
  if (currentUser?.role === 'gm') {
    console.log(
      '[GM Viewer Debug] Task:',
      task.id,
      task.title,
      'isViewer:',
      (task as any).isViewer,
      'assignedTo:',
      task.assignedTo,
      'currentUser:',
      currentUser.id
    );
  }
  const isManagerOrAdmin =
    currentUser?.role === 'manager' || currentUser?.role === 'admin';
  const isAdmin = currentUser?.role === 'admin';
  // GM/PM/HOD can track and complete/approve tasks they created (same as admin portal)
  const isCreatorRole =
    !!currentUser?.id &&
    !!task.createdBy &&
    task.createdBy === currentUser.id &&
    ['gm', 'pm', 'hod', 'dpm'].includes(currentUser?.role || '');
  // For common tasks, any user in assigneeIds can update status
  const assigneeIds = (task as any).assigneeIds as string[] | undefined;
  const isSharedAssignee =
    !!currentUser?.id &&
    Array.isArray(assigneeIds) &&
    assigneeIds.includes(currentUser.id);
  const canCompleteOrUpdateStatus =
    task.assignedTo === currentUser?.id || isCreatorRole || isSharedAssignee;
  // Update Status section: primary assignee or any shared assignee (common task)
  const canUpdateStatusAsAssignee =
    task.assignedTo === currentUser?.id || isSharedAssignee;
  // Drop-task UI enabled for admin
  const enableDropTask = true;
  const isPendingCompletion =
    task.status === 'pending_approval' && !!task.completedAt;
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isExplicitlyExpanded, setIsExplicitlyExpanded] = useState(false);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [isEditingPriority, setIsEditingPriority] = useState(false);
  const [tempPriority, setTempPriority] = useState((task.priority || 0) + 1);
  const [showCommonAssignees, setShowCommonAssignees] = useState(false);

  const handleDropTask = () => {
    const wasDroppedNow = isManagerOrAdmin;
    const alertMessage = wasDroppedNow
      ? `Task "${task.title}" has been successfully dropped!`
      : `Drop request for "${task.title}" has been sent for approval. The task will remain active until approved.`;

    // Show browser alert popup
    alert(alertMessage);

    // Show toast notification
    toast({
      title: wasDroppedNow ? 'Task Dropped' : 'Drop Request Sent',
      description: alertMessage,
      duration: 5000,
    });

    // Call the original status update function
    onStatusUpdate?.(task.id, 'drop');
  };

  // Handle collapse/expand with support for global collapse override
  const handleCollapseToggle = () => {
    if (globalCollapsed) {
      // When globally collapsed, track explicit expansion
      setIsExplicitlyExpanded(!isExplicitlyExpanded);
    } else {
      // Normal local collapse behavior
      setIsCollapsed(!isCollapsed);
    }
  };

  // Determine if task should be collapsed
  // If globally collapsed and explicitly expanded, show expanded
  // Otherwise, use the collapse state (global or local)
  const shouldCollapse = globalCollapsed ? !isExplicitlyExpanded : isCollapsed;

  // Check if task was completed late
  const isCompletedLate =
    task.status === 'complete' &&
    task.completedAt &&
    task.dueDate &&
    new Date(task.completedAt) > new Date(task.dueDate);

  // Priority editing functions
  const handlePriorityEdit = () => {
    setIsEditingPriority(true);
    setTempPriority((task.priority || 0) + 1);
  };

  const handlePrioritySave = () => {
    if (onPriorityUpdate && tempPriority > 0) {
      onPriorityUpdate(task.id, tempPriority - 1);
    }
    setIsEditingPriority(false);
  };

  const handlePriorityCancel = () => {
    setIsEditingPriority(false);
    setTempPriority((task.priority || 0) + 1);
  };

  const handlePriorityKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePrioritySave();
    } else if (e.key === 'Escape') {
      handlePriorityCancel();
    }
  };

  // Drag and drop functionality commented out - can be enabled in future if needed
  // const [{ isDragging, canDrag }, drag] = useDrag(
  //   () => ({
  //     type: 'task',
  //     item: {
  //       id: task.id,
  //       priority: task.priority || 0,
  //       title: task.title,
  //       assignedTo: task.assignedTo,
  //     },
  //     canDrag:
  //       isDraggable &&
  //       !isHelpTask &&
  //       (currentUser?.id === task.assignedTo ||
  //         currentUser?.role === 'manager' ||
  //         currentUser?.role === 'admin'),
  //     collect: monitor => ({
  //       isDragging: monitor.isDragging(),
  //       canDrag: monitor.canDrag(),
  //     }),
  //     end: (_item, monitor) => {
  //       const dropResult = monitor.getDropResult() as any;
  //       if (
  //         dropResult &&
  //         dropResult.priority !== undefined &&
  //         onPriorityUpdate
  //       ) {
  //         onPriorityUpdate(task.id, dropResult.priority);
  //       }
  //     },
  //   }),
  //   [
  //     task.id,
  //     task.priority,
  //     task.title,
  //     task.assignedTo,
  //     isDraggable,
  //     isHelpTask,
  //     currentUser?.id,
  //     currentUser?.role,
  //     onPriorityUpdate,
  //   ]
  // );

  // Placeholder values for commented out drag functionality
  const isDragging = false;
  const canDrag = false;

  // Calculate time remaining and urgency
  // Overdue is considered ONLY after the day *after* due date starts
  // i.e. a task due on Jan 10 becomes overdue from Jan 11 (00:00) onwards.
  const now = new Date();
  const dueDate = new Date(task.dueDate);
  const overdueDeadline = new Date(dueDate);
  overdueDeadline.setDate(overdueDeadline.getDate() + 1);
  overdueDeadline.setHours(0, 0, 0, 0);

  const timeRemaining = overdueDeadline.getTime() - now.getTime();
  const totalTime =
    overdueDeadline.getTime() - new Date(task.createdAt!).getTime();
  const timeRemainingPercent = (timeRemaining / totalTime) * 100;

  const isOverdue = timeRemaining < 0;
  const isUrgent = !isOverdue && timeRemainingPercent < 20;
  const isDueSoon = !isOverdue && !isUrgent && timeRemainingPercent < 40;

  // Check if task is completed
  const isCompleted =
    task.status === 'complete' || task.status === 'pending_approval';

  const getUrgencyBadge = () => {
    // Don't show overdue/urgent badges for completed tasks
    if (isCompleted) {
      return null;
    }
    if (isOverdue) {
      return (
        <Badge variant="destructive" className="text-xs urgent-stamp">
          <AlertTriangle className="w-3 h-3 mr-1" />
          OVERDUE
        </Badge>
      );
    }
    if (isUrgent) {
      return (
        <Badge
          variant="destructive"
          className="text-xs urgent-stamp bg-red-100 text-red-800 hover:bg-red-100"
        >
          <AlertTriangle className="w-3 h-3 mr-1" />
          URGENT
        </Badge>
      );
    }
    if (isDueSoon) {
      return (
        <Badge
          variant="secondary"
          className="text-xs bg-orange-100 text-orange-800 hover:bg-orange-100"
        >
          <Clock className="w-3 h-3 mr-1" />
          DUE SOON
        </Badge>
      );
    }
    return null;
  };

  // Load common assignee details for shared tasks (so we can show all names in "Assigned to")
  const isCommonTask =
    Array.isArray((task as any).assigneeIds) &&
    (task as any).assigneeIds.length > 1;
  const { data: commonAssignees = [] } = useQuery<any[]>({
    queryKey: [`/api/tasks/${task.id}/assignees`],
    enabled: isCommonTask,
  });

  const getStatusColor = () => {
    switch (task.status) {
      case 'pending':
        return 'bg-gray-400';
      case 'started':
        return 'bg-blue-500';
      case 'working':
        return 'bg-orange-500';
      case 'need_more_time':
        return 'bg-red-500';
      case 'need_help':
        return 'bg-yellow-500';
      case 'help_done':
        return 'bg-green-400';
      case 'pending_approval':
        return 'bg-purple-500';
      case 'complete':
        return 'bg-green-500';
      case 'reopened':
        return 'bg-amber-500';
      case 'dropped':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusLabel = () => {
    switch (task.status) {
      case 'need_more_time':
        return 'Need More Time';
      case 'need_help': {
        if (isHelpTask && (task as any).helpRequester) {
          // This is a help request - user is helping someone else
          const requester = (task as any).helpRequester;
          return `Help asked by ${requester.firstName}`;
        } else if (!isHelpTask && (task as any).helpers?.length > 0) {
          // This is the original task - user asked for help from someone
          const helpers = (task as any).helpers;
          const helperNames = helpers
            .map((h: any) => `${h.firstName} ${h.lastName}`)
            .join(', ');
          return `Help requested from ${helperNames}`;
        } else {
          return 'Need Help';
        }
      }
      case 'help_done':
        return 'Help Done';
      case 'pending_approval':
        return 'Pending Approval';
      case 'reopened':
        return 'Reopened';
      default:
        return (
          task.status.charAt(0).toUpperCase() +
          task.status.slice(1).replace('_', ' ')
        );
    }
  };

  const formatTimeRemaining = () => {
    // Don't show overdue message for completed tasks
    if (isCompleted) {
      return 'Completed';
    }
    if (isOverdue) {
      const overdueDays = Math.ceil(
        Math.abs(timeRemaining) / (1000 * 60 * 60 * 24)
      );
      return `${overdueDays} days overdue`;
    }

    const days = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));
    // const percent = Math.max(0, Math.round(timeRemainingPercent));
    // return `${days} days (${percent}%)`;
    return `${days} days`;
  };

  const readOnly = (currentUser as any)?.readOnly;
  const canEdit =
    !readOnly &&
    (currentUser?.role === 'manager' ||
      currentUser?.role === 'admin' ||
      task.assignedTo === currentUser?.id ||
      isCreatorRole ||
      isSharedAssignee);

  const createdByLabel = (() => {
    const creator = (task as any).creator as
      | { firstName: string | null; lastName: string | null; username: string }
      | undefined;
    if (!creator) return null;
    if (creator.firstName || creator.lastName) {
      return `${creator.firstName || ''} ${creator.lastName || ''}`.trim();
    }
    return creator.username || null;
  })();

  const assignedToLabel = (() => {
    const assignee = (task as any).assignee as
      | { firstName: string | null; lastName: string | null; username: string }
      | undefined;
    if (!assignee) return null;
    if (assignee.firstName || assignee.lastName) {
      return `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim();
    }
    return assignee.username || null;
  })();

  // For shared tasks show all assignee names; for single assignee show one name
  const assignedToDisplay = (() => {
    if (isCommonTask && commonAssignees.length > 0) {
      const names = commonAssignees
        .map(
          (u: {
            firstName?: string | null;
            lastName?: string | null;
            username?: string;
          }) =>
            (u.firstName || u.lastName
              ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
              : u.username || '') || ''
        )
        .filter(Boolean);
      return names.length > 0 ? names.join(', ') : assignedToLabel;
    }
    return assignedToLabel;
  })();

  return (
    <Card
      // ref={drag} // Drag and drop commented out - can be enabled in future if needed
      className={`task-card border border-gray-200 dark:border-gray-700 p-2 mb-2 w-full max-w-full ${
        isCompletedLate
          ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600'
          : 'bg-surface'
      } cursor-default ${
        isCompleted
          ? ''
          : isUrgent || isOverdue
            ? 'border-red-300 dark:border-red-600'
            : isDueSoon
              ? 'border-orange-300 dark:border-orange-600'
              : ''
      }`}
      data-testid={`task-card-${task.id}`}
      data-task-id={task.id}
    >
      <CardContent className="p-0">
        {/* Task Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0 pr-2">
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                className="p-1 h-auto text-gray-400 hover:text-gray-600"
                onClick={handleCollapseToggle}
                aria-label={shouldCollapse ? 'Expand task' : 'Collapse task'}
              >
                {shouldCollapse ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
              {/* Priority Number - Editable */}
              <div className="flex-shrink-0">
                {isEditingPriority ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={tempPriority}
                      onChange={e =>
                        setTempPriority(
                          Math.max(1, parseInt(e.target.value) || 1)
                        )
                      }
                      onKeyDown={handlePriorityKeyDown}
                      onBlur={handlePrioritySave}
                      className="w-12 h-6 text-xs font-bold text-center bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded px-1"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="p-0 h-4 w-4 text-green-600 hover:text-green-700"
                      onClick={handlePrioritySave}
                    >
                      <Check className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="p-0 h-4 w-4 text-red-600 hover:text-red-700"
                      onClick={handlePriorityCancel}
                    >
                      ×
                    </Button>
                  </div>
                ) : (
                  // <Badge
                  //   variant="outline"
                  //   className={`priority-badge text-xs font-bold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 min-w-[24px] h-6 flex items-center justify-center ${
                  //     canDrag
                  //       ? 'hover:bg-blue-100 dark:hover:bg-blue-800/30 cursor-pointer'
                  //       : ''
                  //   }`}
                  //   title={`Priority: ${task.priority || 0} - Click to edit`}
                  //   onClick={canDrag ? handlePriorityEdit : undefined}
                  // >
                  //   {(task.priority || 0) + 1}
                  // </Badge>
                  <Badge
                    variant="outline"
                    className="text-xs font-bold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 min-w-[24px] h-6 flex items-center justify-center"
                  >
                    {taskNumber !== undefined ? taskNumber : '#'}
                  </Badge>
                )}
              </div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 break-words flex-1 flex items-center gap-2">
                {task.title}
                {hasUnreadComments && (
                  <span
                    className="inline-flex items-center flex-shrink-0"
                    title="New comment added"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                    </span>
                  </span>
                )}
                <div className="flex items-center space-x-2 ml-auto">
                  {task.timeStarted && (
                    <span className="inline-flex items-center">
                      <div
                        className="w-2 h-2 bg-green-500 rounded-full animate-pulse"
                        title="Timer running"
                      ></div>
                    </span>
                  )}
                </div>
              </h4>
            </div>

            {/* Description / Project / Team - Show even when collapsed */}
            {(task.description || task.project || (task as any).teamId) && (
              <div className="mt-2 space-y-2">
                {task.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {task.description}
                  </p>
                )}
                {/* Team (used as project) */}
                {(task as any).teamId &&
                  (() => {
                    const team = teams.find(
                      (t: any) => t.id === (task as any).teamId
                    );
                    return team ? (
                      <Badge
                        variant="outline"
                        className="text-xs w-fit bg-blue-50 text-blue-700 border-blue-200"
                      >
                        <FolderOpen className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{team.name}</span>
                      </Badge>
                    ) : null;
                  })()}
                {/* Project */}
                {task.project && (
                  <div className="flex flex-col gap-1">
                    <Badge
                      variant="outline"
                      className="text-xs w-fit"
                      style={{
                        backgroundColor: `${task.project.color || '#3B82F6'}20`,
                        color: task.project.color || '#3B82F6',
                        borderColor: `${task.project.color || '#3B82F6'}40`,
                      }}
                    >
                      <FolderOpen className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{task.project.name}</span>
                    </Badge>
                    {!shouldCollapse &&
                    ((task.project.hasAmc && task.project.amcExpiryDate) ||
                      task.project.supportPackageValidityPeriod) ? (
                      <div className="flex flex-wrap gap-1">
                        {task.project.hasAmc && task.project.amcExpiryDate && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            AMC:{' '}
                            {new Date(
                              task.project.amcExpiryDate
                            ).toLocaleDateString()}
                          </span>
                        )}
                        {task.project.supportPackageValidityPeriod && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            Support:{' '}
                            {new Date(
                              task.project.supportPackageValidityPeriod
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
            {/* Created by & Assigned to - Show at top, before description */}
            {(createdByLabel || assignedToDisplay) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {createdByLabel && (
                  <span>
                    Created by{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {createdByLabel}
                    </span>
                  </span>
                )}
                {assignedToDisplay && (
                  <span>
                    Assigned to{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {assignedToDisplay}
                    </span>
                  </span>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-start gap-2 mt-2">
              {/* Recurring Task Indicator */}
              {(task as any).isRecurring && (
                <Badge
                  variant="outline"
                  className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-700"
                >
                  <Repeat className="w-3 h-3 mr-1 flex-shrink-0" />
                  Recurring: {(task as any).recurrenceFrequency || 'Unknown'}
                </Badge>
              )}
              {getUrgencyBadge()}
              {/* Common task indicator (shared between multiple assignees) */}
              {Array.isArray((task as any).assigneeIds) &&
                (task as any).assigneeIds.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setShowCommonAssignees(prev => !prev)}
                    className="focus:outline-none"
                  >
                    <Badge
                      variant="outline"
                      className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-700"
                    >
                      <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                      Common task ({(task as any).assigneeIds.length} users)
                    </Badge>
                  </button>
                )}
              {/* Viewer Badge - Show when GM is viewing this task */}
              {(task as any).isViewer && currentUser?.role === 'gm' && (
                <Badge
                  variant="outline"
                  className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700"
                >
                  <Eye className="w-3 h-3 mr-1 flex-shrink-0" />
                  Viewer
                </Badge>
              )}
            </div>
            {showCommonAssignees &&
              Array.isArray(commonAssignees) &&
              commonAssignees.length > 0 && (
                <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-300">
                  Shared with:{' '}
                  {commonAssignees
                    .map((u: any) =>
                      `${u.firstName || ''} ${u.lastName || ''}`.trim()
                    )
                    .join(', ')}
                </div>
              )}
          </div>
          {/* Three-dot menu commented out - not working */}
          {/* <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 h-auto"
            data-testid={`button-task-menu-${task.id}`}
          >
            <MoreVertical className="w-4 h-4" />
          </Button> */}
        </div>

        {/* Collapsible Content */}
        {!shouldCollapse && (
          <>
            {/* Task Status */}
            <div className="mb-3">
              <div className="flex items-center space-x-2 flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-3 h-3 rounded-full ${getStatusColor()}`}
                  ></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {getStatusLabel()}
                  </span>
                  {isCompletedLate && (
                    <Badge variant="destructive" className="text-xs px-1 py-0">
                      Late
                    </Badge>
                  )}
                </div>
                {/* Viewer Info Banner for GM */}
                {(task as any).isViewer &&
                  currentUser?.role === 'gm' &&
                  task.assignedTo && (
                    <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded border border-blue-200 dark:border-blue-700">
                      <Eye className="w-3 h-3 mr-1" />
                      <span>
                        Viewing task assigned to {task.assignee?.firstName}{' '}
                        {task.assignee?.lastName}
                      </span>
                    </div>
                  )}
              </div>
            </div>

            {/* Task Details */}
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
              <div className="flex items-center justify-between">
                <span>Due Date:</span>
                <span
                  className={`font-medium ${
                    isCompleted
                      ? 'text-gray-900 dark:text-gray-100'
                      : isOverdue
                        ? 'text-red-600 dark:text-red-400'
                        : isUrgent
                          ? 'text-red-600 dark:text-red-400'
                          : isDueSoon
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {new Date(task.dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Time Left:</span>
                <span
                  className={`font-medium ${
                    isCompleted
                      ? 'text-gray-900 dark:text-gray-100'
                      : isOverdue
                        ? 'text-red-600 dark:text-red-400'
                        : isUrgent
                          ? 'text-red-600 dark:text-red-400'
                          : isDueSoon
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {formatTimeRemaining()}
                </span>
              </div>
              {isCompletedLate && task.completedAt && task.dueDate && (
                <div className="flex items-center justify-between">
                  <span>Completed:</span>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {new Date(task.completedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span>Last Updated:</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {task.updatedAt
                    ? new Date(task.updatedAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'UTC',
                      })
                    : 'Unknown'}
                </span>
              </div>
            </div>

            {/* Help Task Indicator */}
            {isHelpTask && (
              <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                <div className="flex items-center text-blue-700">
                  <Users className="w-3 h-3 mr-1" />
                  <span className="font-medium">
                    Helping: {task.assignee?.firstName}{' '}
                    {task.assignee?.lastName}
                  </span>
                </div>
              </div>
            )}

            {/* Help Request Indicators */}
            {task.status === 'need_help' && !isHelpTask && (
              <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                <div className="flex items-start text-yellow-700">
                  <HelpCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">
                      {(task as any).helpers?.length > 0 ? (
                        <span>
                          Help requested from{' '}
                          {(task as any).helpers
                            .map((h: any) => `${h.firstName} ${h.lastName}`)
                            .join(', ')}
                        </span>
                      ) : (
                        <span>Help requested</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Assign to Me Button for Unassigned Tasks */}
            {!task.assignedTo && currentUser && (
              <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                  This task is unassigned
                </div>
                {currentUser.role === 'team_member' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-6 px-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={() => onAssignToMe?.(task.id)}
                    data-testid={`button-assign-to-me-${task.id}`}
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    Assign to Me
                  </Button>
                ) : (
                  <div className="text-xs text-blue-600">
                    Click Edit to assign this task to someone
                  </div>
                )}
              </div>
            )}

            {/* Request Extension Button for Overdue Tasks */}
            {isOverdue &&
              (task.assignedTo === currentUser?.id || isSharedAssignee) &&
              task.status !== 'complete' && (
                <div className="mb-3 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
                  <div className="text-xs font-medium text-orange-700 dark:text-orange-300 mb-2">
                    Task is overdue
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-6 px-2 text-orange-600 border-orange-300 hover:bg-orange-100"
                    onClick={() => {
                      // Open view modal - extension request mode will be handled by parent
                      onView?.(task);
                    }}
                    data-testid={`button-request-extension-${task.id}`}
                  >
                    <CalendarPlus className="w-3 h-3 mr-1" />
                    Request Extension
                  </Button>
                </div>
              )}

            {/* Status Update Buttons — only for assignee (end user), not for creator in "Created by Me" view */}
            {canUpdateStatusAsAssignee && (
              <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Update Status:
                </div>
                <div className="flex flex-wrap gap-1">
                  {task.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-6 px-2"
                      onClick={() => onStatusUpdate?.(task.id, 'started')}
                      data-testid={`button-status-started-${task.id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Start
                    </Button>
                  )}

                  {/* {(task.status === 'started' || task.status === 'working') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-6 px-2"
                      onClick={() => onStatusUpdate?.(task.id, 'working')}
                      data-testid={`button-status-working-${task.id}`}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Working
                    </Button>
                  )} */}

                  {task.status !== 'complete' &&
                    task.status !== 'pending' &&
                    task.status !== 'pending_approval' && (
                      <>
                        {/* <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-6 px-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={() =>
                            onStatusUpdate?.(task.id, 'need_more_time')
                          }
                          data-testid={`button-status-need-more-time-${task.id}`}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Need More Time
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-6 px-2 text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                          onClick={() => onStatusUpdate?.(task.id, 'need_help')}
                          data-testid={`button-status-need-help-${task.id}`}
                        >
                          <HelpCircle className="w-3 h-3 mr-1" />
                          Need Help
                        </Button> */}

                        {task.status === 'need_help' &&
                          ((task as any).helpers?.some(
                            (helper: any) => helper.id === currentUser?.id
                          ) ||
                            isHelpTask) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-6 px-2 text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() =>
                                onStatusUpdate?.(task.id, 'help_done')
                              }
                              data-testid={`button-status-help-done-${task.id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Help Done
                            </Button>
                          )}

                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-6 px-2 text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => {
                            if (task.isRecurring) {
                              setIsCompletionModalOpen(true);
                            } else {
                              onStatusUpdate?.(task.id, 'complete');
                            }
                          }}
                          data-testid={`button-status-complete-${task.id}`}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Complete
                        </Button>
                      </>
                    )}
                </div>
              </div>
            )}

            {/* Help Done Button for Help Tasks */}
            {task.status === 'need_help' &&
              (currentUser?.role === 'admin' ||
                currentUser?.role === 'manager' ||
                currentUser?.role === 'team_member' ||
                currentUser?.role === 'gm' ||
                currentUser?.role === 'pm' ||
                currentUser?.role === 'hod' ||
                currentUser?.role === 'dpm') &&
              ((task as any).helpers?.some(
                (helper: any) => helper.id === currentUser?.id
              ) ||
                isHelpTask) && (
                <div className="mb-3 p-2 bg-green-50 dark:bg-green-900 rounded-lg border border-green-200 dark:border-green-700">
                  <div className="text-xs font-medium text-green-700 dark:text-green-300 mb-2">
                    Help provided - Mark as Help Done
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-6 px-2 text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => onStatusUpdate?.(task.id, 'help_done')}
                      data-testid={`button-status-help-done-${task.id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Help Done
                    </Button>
                  </div>
                </div>
              )}

            {/* Approval Buttons for Managers/Admins or Creator (GM/PM/HOD) */}
            {task.status === 'pending_approval' &&
              (isManagerOrAdmin || isCreatorRole) && (
                <div className="mb-3 p-2 bg-purple-50 dark:bg-purple-900 rounded-lg border border-purple-200 dark:border-purple-700">
                  <div className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-2">
                    {isPendingCompletion
                      ? `Task completed by ${task.assignee?.firstName} ${task.assignee?.lastName} - Awaiting your approval`
                      : `Drop requested by ${task.assignee?.firstName} ${task.assignee?.lastName} - Awaiting your approval`}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-6 px-2 text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => onStatusUpdate?.(task.id, 'approve')}
                      data-testid={`button-approve-${task.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                    {/* Managers, admins, or task creator (GM/PM/HOD) can disapprove */}
                    {(currentUser?.role === 'manager' ||
                      currentUser?.role === 'admin' ||
                      isCreatorRole) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-6 px-2 text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => onStatusUpdate?.(task.id, 'disapprove')}
                        data-testid={`button-disapprove-${task.id}`}
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Disapprove
                      </Button>
                    )}
                  </div>
                </div>
              )}

            {/* Drop Task: admin or task creator (same as admin) */}
            {enableDropTask &&
              task.status !== 'dropped' &&
              task.status !== 'complete' &&
              task.status !== 'pending_approval' &&
              (isAdmin || task.createdBy === currentUser?.id) && (
                <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Task Management Actions
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-6 px-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                          data-testid={`button-drop-${task.id}`}
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Drop Task
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-orange-600">
                            Are you sure you want to drop this task?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This action will mark the task as "dropped" and
                            notify the assigned user. The task will no longer be
                            active and cannot be easily restored.
                            <br />
                            <br />
                            <strong>Task:</strong> {task.title}
                            {task.assignedTo && (
                              <>
                                <br />
                                <strong>Assigned to:</strong>{' '}
                                {task.assignee?.firstName}{' '}
                                {task.assignee?.lastName}
                              </>
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDropTask}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                          >
                            Yes, Drop Task
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}

            {/* Task Actions */}
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-1">
                {/* Quick Timer Toggle */}
                {/* {canTrackTime && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`text-xs p-1 h-6 min-w-0 ${
                      task.timeStarted
                        ? 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                    onClick={() => {
                      if (task.timeStarted) {
                        // Quick stop timer - could open a simple dialog
                        onView?.(task); // Open task view to stop timer
                      } else {
                        // Quick start timer
                        onView?.(task); // Open task view to start timer
                      }
                    }}
                    title={
                      task.timeStarted
                        ? 'Timer running - Click to stop'
                        : 'Click to start timer'
                    }
                  >
                    {task.timeStarted ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
                        <span className="truncate">Timer</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">Timer</span>
                      </>
                    )}
                  </Button>
                )} */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 h-6 min-w-0"
                  onClick={() => onView?.(task)}
                  data-testid={`button-view-${task.id}`}
                >
                  <Eye className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">View</span>
                </Button>
                {/* {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 h-6 min-w-0"
                    onClick={() => onEdit?.(task)}
                    data-testid={`button-edit-${task.id}`}
                  >
                    <Edit className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="truncate">Edit</span>
                  </Button>
                )} */}
                {/* SOS Help Button - show for assigned user or when user can edit and not help tasks */}
                {/* {(task.assignedTo === currentUser?.id || canEdit) &&
                  !isHelpTask && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 h-6 min-w-0"
                      onClick={() => onNeedHelp?.(task)}
                      data-testid={`button-sos-${task.id}`}
                      title="Request Help"
                    >
                      <HelpCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">SOS</span>
                    </Button>
                  )} */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-1 h-6 min-w-0 underline"
                  onClick={() => setIsHistoryOpen(true)}
                  data-testid={`button-history-${task.id}`}
                >
                  <History className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">History</span>
                </Button>
                {/* Comment prompt: red bg + white text when unread, "Add comment" when read - on right of History */}
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-xs p-1 h-6 min-w-0 ${
                    hasUnreadComments
                      ? 'bg-red-600 text-white hover:bg-red-700 hover:text-white font-medium animate-pulse'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                  onClick={() => onView?.(task)}
                  title={
                    hasUnreadComments
                      ? 'New comment added - click to view'
                      : 'Add a comment'
                  }
                >
                  <MessageCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    {hasUnreadComments ? 'Comment added' : 'Add comment'}
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Task History Modal */}
      <TaskHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        taskId={task.id}
        taskTitle={task.title}
      />

      {/* Recurring Task Completion Modal */}
      <RecurringTaskCompletionModal
        isOpen={isCompletionModalOpen}
        onClose={() => setIsCompletionModalOpen(false)}
        task={task}
        onSuccess={() => {
          // Trigger a refresh by calling the status update
          onStatusUpdate?.(task.id, 'complete');
        }}
      />
    </Card>
  );
}
